import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Aviso, Botao, Campo, Entrada } from '@via-tax/ui'
import { useEffect, useState, type FormEvent } from 'react'
import { mensagemDeErro, useTRPC } from '../trpc'
import { TelaEntrada } from './Login'

const soDigitos = (v: string) => v.replace(/[^0-9]/g, '').slice(0, 6)

function useVoltarAoContexto() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: trpc.sessao.contexto.queryKey() })
}

export function TotpInscrever() {
  const trpc = useTRPC()
  const voltar = useVoltarAoContexto()
  const [codigo, setCodigo] = useState('')
  const iniciar = useMutation(trpc.sessao.totpIniciar.mutationOptions())
  const confirmar = useMutation(trpc.sessao.totpConfirmar.mutationOptions())
  const sair = useMutation(trpc.sessao.logout.mutationOptions({ onSuccess: voltar }))

  const { mutate: iniciarMutate } = iniciar
  useEffect(() => iniciarMutate(), [iniciarMutate])

  if (confirmar.data) {
    const codigos = confirmar.data.codigosRecuperacao
    return (
      <TelaEntrada titulo="Guarde os códigos de recuperação" largura="max-w-md">
        <p className="text-sm leading-relaxed text-tinta-2">
          São dez códigos de uso único para entrar se você perder o celular. Eles não serão exibidos de novo — guarde fora
          deste computador.
        </p>
        <ol className="num mt-4 grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-md border border-linha bg-fundo p-4 text-[13px]">
          {codigos.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ol>
        <div className="mt-5 flex gap-2">
          <Botao onClick={() => void navigator.clipboard.writeText(codigos.join('\n'))}>Copiar</Botao>
          <Botao variante="primario" onClick={voltar}>
            Guardei, continuar
          </Botao>
        </div>
      </TelaEntrada>
    )
  }

  return (
    <TelaEntrada titulo="Configure o segundo fator" largura="max-w-md">
      <p className="text-sm leading-relaxed text-tinta-2">
        Usuários do escritório entram sempre com código do aplicativo autenticador (Google Authenticator, Microsoft
        Authenticator, 1Password, Bitwarden). Escaneie o QR Code e digite o código de 6 dígitos.
      </p>
      <div className="mt-5 flex flex-wrap items-start gap-5">
        <div className="grid size-[172px] shrink-0 place-items-center rounded-md border border-linha bg-white p-2">
          {iniciar.data ? (
            <img src={iniciar.data.qrDataUrl} alt="QR Code para o aplicativo autenticador" width={156} height={156} />
          ) : (
            <span className="text-xs text-tinta-3">{iniciar.isError ? 'Falhou' : 'Gerando…'}</span>
          )}
        </div>
        <form
          className="flex min-w-[180px] flex-1 flex-col gap-3"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            confirmar.mutate({ codigo })
          }}
        >
          <Campo rotulo="Código do aplicativo">
            <Entrada
              value={codigo}
              onChange={(e) => setCodigo(soDigitos(e.target.value))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              className="num text-lg tracking-[0.3em]"
            />
          </Campo>
          <Botao type="submit" variante="primario" disabled={codigo.length !== 6 || confirmar.isPending}>
            {confirmar.isPending ? 'Confirmando…' : 'Ativar segundo fator'}
          </Botao>
        </form>
      </div>
      {iniciar.data && (
        <p className="mt-4 text-xs text-tinta-3">
          Sem câmera? Digite a chave: <span className="num text-tinta-2">{iniciar.data.chaveManual}</span>
        </p>
      )}
      {(confirmar.isError || iniciar.isError) && (
        <div className="mt-4">
          <Aviso>{mensagemDeErro(confirmar.error ?? iniciar.error)}</Aviso>
        </div>
      )}
      <button onClick={() => sair.mutate()} className="mt-5 text-xs text-tinta-3 underline underline-offset-4 hover:text-tinta">
        Sair e entrar com outro usuário
      </button>
    </TelaEntrada>
  )
}

export function TotpVerificar() {
  const trpc = useTRPC()
  const voltar = useVoltarAoContexto()
  const [codigo, setCodigo] = useState('')
  const [recuperacao, setRecuperacao] = useState(false)
  const verificar = useMutation(trpc.sessao.totpVerificar.mutationOptions({ onSuccess: voltar }))
  const recuperar = useMutation(trpc.sessao.recuperacaoVerificar.mutationOptions({ onSuccess: voltar }))
  const pendente = verificar.isPending || recuperar.isPending
  const erro = verificar.error ?? recuperar.error

  return (
    <TelaEntrada titulo="Segundo fator">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault()
          if (recuperacao) recuperar.mutate({ codigo: codigo.trim() })
          else verificar.mutate({ codigo })
        }}
      >
        <Campo rotulo={recuperacao ? 'Código de recuperação' : 'Código do aplicativo autenticador'}>
          <Entrada
            value={codigo}
            onChange={(e) => setCodigo(recuperacao ? e.target.value.toUpperCase().slice(0, 11) : soDigitos(e.target.value))}
            inputMode={recuperacao ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            placeholder={recuperacao ? 'XXXXX-XXXXX' : '000000'}
            className="num text-lg tracking-[0.25em]"
            autoFocus
          />
        </Campo>
        {erro && <Aviso>{mensagemDeErro(erro)}</Aviso>}
        <Botao type="submit" variante="primario" disabled={pendente || codigo.length < 6}>
          {pendente ? 'Verificando…' : 'Entrar'}
        </Botao>
        <button
          type="button"
          onClick={() => {
            setRecuperacao(!recuperacao)
            setCodigo('')
          }}
          className="text-left text-xs text-tinta-3 underline underline-offset-4 hover:text-tinta"
        >
          {recuperacao ? 'Usar o aplicativo autenticador' : 'Perdi o celular — usar código de recuperação'}
        </button>
      </form>
    </TelaEntrada>
  )
}
